// Wiederverwendbarer Deep-Link-Router für Vollseiten-Unterseiten (Pre.750).
//
// Zweck (DRY): Jede Unterseite (Essenz-Spiegel, Mein Leben, …) ist reload-fest
// und teilbar, ohne ihre Hash-Logik selbst zu kopieren. Eine Seite registriert
// nur ihren Hash-Schlüssel + open/close/isOpen — EIN gemeinsamer hashchange-
// Listener synchronisiert alle. (Human Design hat eine eigene, ältere Hash-
// Logik mit Admin-User-Parametern und bleibt bewusst dort — siehe app.ts.)

interface PageRoute {
  /** Hash ohne „#", z.B. "essence" → Deep-Link "#essence". */
  key: string;
  /** Liegt diese Seite gerade offen vor? (DOM-Abfrage) */
  isOpen: () => boolean;
  /** Seite öffnen (Reload/Deep-Link/Vor-Zurück). */
  open: () => void;
  /** Seite schließen (Hash wurde entfernt). */
  close: () => void;
}

const routes: PageRoute[] = [];

/** Eine Unterseite registrieren. Mehrfach-Registrierung desselben Keys ignoriert. */
export function registerPageRoute(route: PageRoute): void {
  if (routes.some((r) => r.key === route.key)) return;
  routes.push(route);
}

/** Deep-Link setzen (reload-fest, KEIN hashchange → kein Open/Close-Loop). */
export function setPageHash(key: string): void {
  const h = `#${key}`;
  if (location.hash !== h)
    window.history.replaceState(
      null,
      "",
      location.pathname + location.search + h,
    );
  // replaceState feuert KEIN hashchange — Navigation explizit melden
  // (Sidebar-Markierung u.a. haengen daran).
  window.dispatchEvent(new Event("voice-v8:nav-changed"));
}

/** Deep-Link wieder entfernen (nur wenn er gerade auf diese Seite zeigt). */
export function clearPageHash(key: string): void {
  if (location.hash === `#${key}`)
    window.history.replaceState(null, "", location.pathname + location.search);
  window.dispatchEvent(new Event("voice-v8:nav-changed"));
}

/**
 * Kennt der Router diesen Hash?
 *
 * 08.09.2026 — beim Start muss unterschieden werden: ein Seiten-Hash darf
 * ueber der wiederhergestellten Grundansicht geoeffnet werden, ein fremder
 * (Human Design hat seine eigene, aeltere Logik) bleibt unangetastet.
 */
export function istSeitenHash(hash: string): boolean {
  const key = hash.replace(/^#/, "");
  return key.length > 0 && routes.some((r) => r.key === key);
}

/**
 * Jede angemeldete Seite zu, jeder Seiten-Hash weg. (27.09.2026, Ticket 2637)
 *
 * Für den Wechsel in eine Grundansicht (`closeAllOverlays` in app.ts). Dort
 * stand bis pre.3384 eine eigene Liste der Seiten-Hashes — die neue Seite
 * „Ankermomente“ fehlte darin: nach „Chat“ lag sie weiter obenauf, `#anker`
 * blieb stehen, die Leiste markierte sie. Der Router kennt jede Seite schon,
 * also schließt er sie auch; eine zweite Liste gibt es nicht mehr.
 *
 * ⚠️ `close()` nur für Seiten, die noch offen sind — was app.ts vorher
 *    schon entfernt hat, fasst das hier nicht noch einmal an.
 */
export function schliesseAlleSeiten(): void {
  for (const r of routes) {
    if (r.isOpen()) r.close();
    clearPageHash(r.key);
  }
}

/**
 * Liegt gerade eine angemeldete Seite (Mein Alltag, Spiegel …) offen? (11.10.2026, Ticket 2945)
 *
 * Für den Aufnahme-Start: ein Aufruf soll den Chat nach vorn holen, auch wenn die Grundansicht
 * schon „Chat“ heißt und eine Vollseite darüber liegt.
 */
export function eineSeiteOffen(): boolean {
  return routes.some((r) => r.isOpen());
}

/**
 * Eine angemeldete Unterseite von außen öffnen (Zuhause „Neu“, 13.10.2026). Gibt es sie nicht, passiert nichts
 * und die Antwort ist false — der Aufrufer zeigt dann gar keinen Knopf dorthin.
 */
export function oeffneSeite(key: string): boolean {
  const r = routes.find((x) => x.key === key);
  if (!r) return false;
  if (!r.isOpen()) r.open();
  return true;
}

/** Beim Reload/Deep-Link die passende Unterseite öffnen (höchstens eine). */
export function openPagesFromHash(): void {
  for (const r of routes) {
    if (location.hash === `#${r.key}` && !r.isOpen()) {
      r.open();
      return;
    }
  }
}

/** EIN hashchange-Listener für alle Routen: Vor/Zurück + manuelle Hash-Änderung. */
export function initPageRouter(): void {
  window.addEventListener("hashchange", () => {
    for (const r of routes) {
      const onPage = location.hash === `#${r.key}`;
      if (onPage && !r.isOpen()) r.open();
      else if (!onPage && r.isOpen()) r.close();
    }
  });
}
